/**
 * Pure helpers behind `/tools/encode/hex`. Tests in
 * `tests/web/hexTool.test.ts`.
 *
 * Concretely we expose four entry points that cover the cases people
 * actually paste into a hex tool:
 *   - text → hex string
 *   - hex string → text (UTF-8 decode, with a flag if it isn't UTF-8)
 *   - bytes → hex string (used internally; exported for completeness)
 *   - hex string → byte array
 *
 * Hex output is lowercase by default with no separators. The
 * decoder is permissive about whitespace, '0x' prefixes and ':' /
 * '-' delimiters because those show up everywhere in real-world hex
 * dumps.
 */

export interface HexEncodeOptions {
  uppercase?: boolean;
  /** Insert this string between every byte. e.g. ' ' for 'de ad be ef'. */
  separator?: string;
}

const HEX_LO = '0123456789abcdef';
const HEX_HI = '0123456789ABCDEF';

export function encodeHexText(text: string, opts: HexEncodeOptions = {}): string {
  return encodeHexBytes(new TextEncoder().encode(text), opts);
}

export function encodeHexBytes(bytes: Uint8Array, opts: HexEncodeOptions = {}): string {
  const alpha = opts.uppercase ? HEX_HI : HEX_LO;
  const sep = opts.separator ?? '';
  if (bytes.length === 0) return '';
  // Pre-size the result to avoid string-concat reallocation cost on
  // multi-megabyte inputs (e.g. dragged-in binaries from a future
  // version of the tool).
  const parts = new Array<string>(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i];
    parts[i] = alpha[v >> 4] + alpha[v & 0x0f];
  }
  return parts.join(sep);
}

export type HexDecodeResult =
  | { ok: true; bytes: Uint8Array; text: string }
  | { ok: false; error: string };

export function decodeHex(input: string): HexDecodeResult {
  // 1. Strip whitespace, ':' and '-' separators users tend to paste.
  // 2. Drop a leading '0x' / '0X' if present.
  let cleaned = input.replace(/[\s:-]+/g, '');
  if (cleaned.length === 0) return { ok: false, error: '输入为空' };
  if (cleaned.startsWith('0x') || cleaned.startsWith('0X')) {
    cleaned = cleaned.slice(2);
  }

  if (cleaned.length === 0) return { ok: false, error: '输入只有 "0x" 前缀' };
  if (cleaned.length % 2 !== 0) {
    return { ok: false, error: 'Hex 长度必须是偶数（每字节 2 位）' };
  }

  const bad = cleaned.match(/[^0-9a-fA-F]/);
  if (bad) {
    return { ok: false, error: `非法 Hex 字符: '${bad[0]}'` };
  }

  const bytes = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
  }

  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    text = '<二进制数据，UTF-8 解码失败>';
  }

  return { ok: true, bytes, text };
}
